import React from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import PropTypes from 'prop-types';
import { DateTime } from 'luxon';
import styled from 'styled-components';

import withStyles from '@mui/styles/withStyles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Dialog from '@mui/material/Dialog';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import FormControlLabel from '@mui/material/FormControlLabel';
import Tooltip from '@mui/material/Tooltip';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import ClickAwayListener from '@mui/material/ClickAwayListener';

// mui icons
import ConvertIcon from '@mui/icons-material/OfflineBolt';
import DeleteIcon from '@mui/icons-material/Delete'; // or Close

import { removeInquiry } from '../store/slices/inquiriesSlice';
import { setIntendedInquiryId, setInitialCallFormPrimingInfo } from '../store/slices/intendedInfoSlice';
import { setPropertyFilterState } from '../store/slices/filterStatesSlice';
import { setApiResponseInfo } from '../store/slices/apiResponseInfoSlice';

import { dateStringForDateTime, createStdRequest, apiFetch } from '../utils/utils';
import { isNonLocalNonLoggedInVisitor } from '../utils/utils';
import {
  getPropertyFilterMenuItems,
  allMenuItemsAreChecked,
  getDefaultPropertyFilterMenuItems,
  filterIsInDefaultState,
} from '../utils/utils';
import createHcmForPageAndDescriptions from '../utils/HideableColumnManager';

import DA from '../store/ReduxAccess';
import ReduxLoader from '../store/ReduxLoader';
import SocketAccess from '../comm/SocketAccess';

import { allevaOrangeColor, allevaSlateColor } from '../md/assets/jss/material-dashboard-pro-react';

import InquiryTableAppBar from './InquiryTableAppBar';
import ColumnFilterIcon from './ColumnFilterIcon';
import NewInquiryModal from './NewInquiryModal';
import InquiryConversionModal from './InquiryConversionModal';
import ConfirmModal from './ConfirmModal';

import pageTableStyles from '../assets/jss/crm/components/pageTableStyles.jsx';
import tableHeadStyles from '../assets/jss/crm/components/tableHeadStyles.jsx';

function desc(a, b, extractor) {
  if (extractor(b) < extractor(a)) {
    return -1;
  }
  if (extractor(b) > extractor(a)) {
    return 1;
  }
  return 0;
}

function sortableValueExtractorForColumn(columnId) {
  switch (columnId) {
    case 'date':
      return (lead) => lead.date.valueOf();
    case 'owner':
      return (lead) => {
        const person = DA.getPersonById(lead.ownerPersonId);
        return person ? person.preferredFullName : 'zzzz';
      };
    case 'referralType':
      return (lead) => lead.referralSourceCategory || 'zzzz';
    case 'referralOrgType':
      return (lead) => lead.referralOrgType || 'zzzz';
    case 'referralCompany':
      return (lead) => (lead.referralOrgId ? DA.getFirmById(lead.referralOrgId).name : 'zzzz');
    case 'referralContact':
      return (lead) => {
        const person = DA.getPersonById(lead.referralContactId);
        return person ? person.preferredFullName : 'zzzz';
      };
    default:
      return (lead) => lead[columnId];
  }
}

function stableSort(array, cmp) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = cmp(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

function getSortingFunction(order, columnToOrderBy) {
  const extractor = sortableValueExtractorForColumn(columnToOrderBy);
  return order === 'desc' ? (a, b) => desc(a, b, extractor) : (a, b) => -desc(a, b, extractor);
}

const allColumnDescrips = [
  { id: 'name', label: 'Name' },
  { id: 'date', numeric: true, label: 'Date' },
  { id: 'owner', hideable: true, filterable: true, label: 'Owner', initiallyShown: true },
  { id: 'temperature', hideable: true, filterable: true, label: 'Temp', initiallyShown: true },
  {
    id: 'referralType',
    hideable: true,
    filterable: true,
    numeric: false,
    label: 'Referral Type',
    initiallyShown: true,
  },
  {
    id: 'referralOrgType',
    hideable: true,
    filterable: false, // should be true, but we don't have a real spec yet
    numeric: false,
    label: 'Referral Organization Type',
    initiallyShown: true,
  },
  { id: 'referralCompany', hideable: true, label: 'Referral Company', initiallyShown: false },
  { id: 'referralContact', hideable: true, label: 'Referral Contact', initiallyShown: false },
  { id: 'message', label: 'Message', notSortable: true },
  { id: 'actions', label: 'Actions', notSortable: true },
];

function handleEntered() {
  document.getElementsByTagName('body')[0].style.paddingRight = '0px';
}

class InquiryTableHead extends React.Component {
  state = {};
  createSortHandler = (property) => (event) => {
    this.props.onRequestSort(event, property);
  };

  createFilterHandler = (property) => (event) => {
    this.props.onRequestFilter(event, property);
  };

  render() {
    const { classes, hcm, order, columnToOrderBy, shouldHighlightFilterIcon } = this.props;

    return (
      <TableHead>
        <TableRow className={classes.headerRow}>
          {hcm.visibleColumnDescrips.map(
            (column) => (
              <TableCell
                key={column.id}
                align={column.numeric ? 'right' : 'left'}
                sortDirection={columnToOrderBy === column.id ? order : false}
              >
                {column.filterable && (
                  <ColumnFilterIcon
                    onClick={this.createFilterHandler(column.id)}
                    shouldHighlight={() => shouldHighlightFilterIcon(column.id)}
                  />
                )}

                {column.label}

                {!column.notSortable && (
                  <Tooltip title="Sort" placement={column.numeric ? 'bottom-end' : 'bottom-start'} enterDelay={300}>
                    <TableSortLabel
                      active={columnToOrderBy === column.id}
                      direction={columnToOrderBy === column.id ? order : 'asc'}
                      onClick={this.createSortHandler(column.id)}
                      classes={{
                        icon: columnToOrderBy === column.id ? classes.activeSortIcon : classes.inactiveSortIcon,
                      }}
                    />
                  </Tooltip>
                )}
              </TableCell>
            ),
            this
          )}
        </TableRow>
      </TableHead>
    );
  }
}

InquiryTableHead.propTypes = {
  onRequestSort: PropTypes.func.isRequired,
  order: PropTypes.string.isRequired,
  columnToOrderBy: PropTypes.string.isRequired,
};

const StyledTableHead = withStyles(tableHeadStyles)(InquiryTableHead);

const styles = (theme) => ({
  ...pageTableStyles(theme),
  actionButton: {
    margin: 0,
    padding: '5px',
    '& svg,& .fab,& .fas,& .far,& .fal,& .material-icons': {
      marginRight: '0px',
    },
    justifyContent: 'left',
    minWidth: '20px',
  },
  icon: {
    verticalAlign: 'middle',
    width: '18px !important',
    height: '18px !important',
    top: '-1px',
    position: 'relative',
  },
  convertIcon: {
    color: allevaOrangeColor[0],
  },
  deleteIcon: {
    color: allevaSlateColor,
  },
  filterListItem: {
    paddingTop: '0px',
    paddingBottom: '0px',
  },
  selectAllButton: {
    textTransform: 'none',
    fontSize: '18px',
  },
  filterLabel: {
    color: '#6b6b6b',
  },
  messageTooltip: {
    fontSize: '15.31px',
  },
});

class InquiryTable extends React.Component {
  state = {
    order: 'desc',
    columnToOrderBy: 'date',
    includeCompanies: true,
    includeOthers: true,
    autocompleteValue: '',
    shouldShowNewInquiryModal: false,
    shouldShowConversionModal: false,
    inquiryToConvert: 0,
    showFilter: false,
    filterItemList: [],
    showFilter: false,
    redisplayToggle: false,
    filterBy: '',
  };

  referralTypes = [
    'Company / Organization',
    'Word of Mouth',
    'Internet Search',
    'Social Media',
    'Media',
    'Marketing Event/Campaign',
    'Unknown',
  ];

  hcm = createHcmForPageAndDescriptions('leadsPage', allColumnDescrips);

  ownerFilterMenuItems = this.getOwnerFilterMenuItems();
  temperatureFilterMenuItems = this.getTemperatureFilterMenuItems();
  referralTypeFilterMenuItems = this.getReferralTypeFilterMenuItems();

  defaultTemperatureFilterMenuItems = this.getDefaultMenuItems('Lead Temperature');
  defaultReferralTypeFilterMenuItems = this.getDefaultReferralTypeFilterMenuItems();

  componentDidMount() {
    const redisplayCallbackStruct = {
      kind: 'inquiry',
      pageName: 'LeadsPage',
      refreshFunc: () => this.redisplayTable(),
    };
    SocketAccess.registerRefreshCallback(redisplayCallbackStruct);

    this.temperatureFilterMenuItems = this.getTemperatureFilterMenuItems();
    this.referralTypeFilterMenuItems = this.getReferralTypeFilterMenuItems();

    this.hcm.loadColumnVisibilityFromRedux();

    this.setState({ redisplayToggle: !this.state.redisplayToggle });

    this.hcm.loadColumnVisibilityFromRedux();

    this.setState({ redisplayToggle: !this.state.redisplayToggle });

    // this.fetchDataAndDisplay();
  }

  componentWillUnmount() {
    this.hcm.constructAndSaveReduxState();
    SocketAccess.unregisterRefreshCallbacksForPage('LeadsPage');
  }

  fetchDataAndDisplay() {
    const firmId = DA.getFirmId();
    const uri = `/api/inquiry/firm/${firmId}`;
    const request = createStdRequest(uri);
    const actionOnGoodResponse = (json) => {
      json.forEach((inquiry) => ReduxLoader.updateInquiry(inquiry));
    };
    apiFetch(this, request, actionOnGoodResponse);
  }

  redisplayTable() {
    this.setState({ redisplayToggle: !this.state.redisplayToggle });
  }

  getOwnerFilterMenuItems() {
    const allUsers = DA.getAllFirmUsers();
    const items = allUsers.map((user) => {
      const person = DA.getPersonById(user.personId);
      if (person) {
        return {
          name: person.preferredFullName,
          personId: user.personId,
          checked: true,
        };
      } else {
        // TODO this is a hack. Do something to resolve the cause that we're working around here.
        return null;
      }
    });
    const selectAllItem = {
      name: 'Unselect All',
      checked: true,
    };
    const unassignedItem = {
      name: 'Unassigned',
      personId: 0,
      checked: true,
    };
    const allItems = [selectAllItem, unassignedItem, ...items];
    return allItems.filter((item) => !!item); // remove the nulls
  }

  getTemperatureFilterMenuItems() {
    const pageFiltersState = this.props.reduxPropertyFiltersState['leadsPage'];
    return getPropertyFilterMenuItems(pageFiltersState, 'temperature', 'Lead Temperature');
  }

  getDefaultMenuItems(dropdownName) {
    return getDefaultPropertyFilterMenuItems(dropdownName);
  }

  getDefaultReferralTypeFilterMenuItems() {
    return this.referralTypes.map((t) => {
      return {
        name: t,
        checked: true,
      };
    });
  }

  getReferralTypeFilterMenuItems() {
    const pageFiltersState = this.props.reduxPropertyFiltersState['leadsPage'] || {};
    const savedItems = pageFiltersState.referralType || [];
    let constructedArray = [];
    this.referralTypes.forEach((t) => {
      const savedItem = savedItems.find((i) => i.name === t);
      if (savedItem) {
        constructedArray = [...constructedArray, savedItem];
      } else {
        const trueItem = { name: t, checked: true };
        constructedArray = [...constructedArray, trueItem];
      }
    });

    const allSelected = allMenuItemsAreChecked(constructedArray);
    const selectionButtonLabel = allSelected ? 'Unselect All' : 'Select All';

    return [{ name: selectionButtonLabel }, ...constructedArray];
  }
  handleRequestSort = (event, property) => {
    const columnToOrderBy = property;
    let order = 'desc';

    if (this.state.columnToOrderBy === property && this.state.order === 'desc') {
      order = 'asc';
    }

    this.setState({ order, columnToOrderBy });
  };

  handleInquiryClick = (inquiry) => {
    this.showInquiryPage(inquiry.inquiryId);
  };

  showNewInquiryModal() {
    this.setState({ shouldShowNewInquiryModal: true });
  }

  closeNewInquiryModal() {
    this.setState({ shouldShowNewInquiryModal: false });
  }

  showInquiryPage(inquiryId) {
    this.props.setIntendedInquiryId(inquiryId);
    this.setState({ redirect: 'lead' });
  }

  convertInquiry(conversionType) {
    const primingInfo = {
      date: DateTime.local().toISO(),
      inquiryId: this.state.inquiryToConvert,
      callRailCallId: 0,
      disposition: conversionType,
    };
    this.props.setInitialCallFormPrimingInfo(primingInfo);
    this.setState({ redirect: 'newProspectPage' });
  }

  showConversionModal(inquiryId) {
    this.setState({ shouldShowConversionModal: true, inquiryToConvert: inquiryId });
  }

  handleRequestFilter = (event, columnId) => {
    const { clientX, clientY } = event;
    let filterItemList = [];

    if (columnId === 'owner') {
      filterItemList = this.ownerFilterMenuItems;
    } else if (columnId === 'temperature') {
      filterItemList = this.temperatureFilterMenuItems;
    } else if (columnId === 'referralType') {
      filterItemList = this.referralTypeFilterMenuItems;
    }

    this.setState({
      showFilter: true,
      filterX: clientX,
      filterY: clientY,
      filterItemList,
      filterBy: columnId,
    });
  };

  handleFilterItemChange = (index) => {
    const copiedList = this.state.filterItemList.slice(0);
    copiedList[index].checked = !this.state.filterItemList[index].checked;
    const payload = {
      pageName: 'leadsPage',
      filterName: this.state.filterBy, // 'owner' or 'temperature'
      propertyName: copiedList[index].name,
      value: copiedList[index].checked,
    };
    this.props.setPropertyFilterState(payload);

    const allSelected = allMenuItemsAreChecked(copiedList.slice(1));
    const selectionButtonLabel = allSelected ? 'Unselect All' : 'Select All';
    copiedList[0].name = selectionButtonLabel;

    this.setState({ filterItemList: copiedList });
  };

  shouldHighlightFilterIcon(columnId) {
    if (columnId === 'owner') {
      return this.shouldHighlightOwnerFilterIcon();
    } else if (columnId === 'temperature') {
      return this.shouldHighlightTemperatureFilterIcon();
    } else if (columnId === 'referralType') {
      return this.shouldHighlightReferralTypeFilterIcon();
    }
  }

  shouldHighlightOwnerFilterIcon() {
    return !this.filterItemsAreAllSelected(this.ownerFilterMenuItems);
  }

  filterItemsAreAllSelected(itemList) {
    let foundUnselectedItem = false;
    itemList.forEach((item) => {
      if (!item.checked) {
        foundUnselectedItem = true;
      }
    });
    return !foundUnselectedItem;
  }

  shouldHighlightTemperatureFilterIcon() {
    return !filterIsInDefaultState(this.temperatureFilterMenuItems, this.defaultTemperatureFilterMenuItems);
  }

  shouldHighlightReferralTypeFilterIcon() {
    return !filterIsInDefaultState(this.referralTypeFilterMenuItems, this.defaultReferralTypeFilterMenuItems);
  }

  handleSelectAllButtonClick = (itemName) => {
    const copiedList = this.state.filterItemList.slice(0);
    let shouldBeChecked;
    if (itemName === 'Select All') {
      copiedList[0].name = 'Unselect All';
      shouldBeChecked = true;
    } else {
      copiedList[0].name = 'Select All';
      shouldBeChecked = false;
    }

    copiedList.forEach((item) => {
      if (!item.name.endsWith('elect All')) {
        item.checked = shouldBeChecked;
      }
      const payload = {
        pageName: 'leadsPage',
        filterName: this.state.filterBy,
        propertyName: item.name,
        value: shouldBeChecked,
      };
      this.props.setPropertyFilterState(payload);
    });

    this.setState({ filterItemList: copiedList });
  };

  handleFilterClickAway = () => {
    this.setState({ showFilter: false });
  };

  applyFilter(entries) {
    return entries.filter((e) => this.passFilter(e));
  }

  passFilter(entry) {
    return (
      entry &&
      (!this.hcm.columnIsVisible('owner') || this.isOwnerFilterMenuItemChecked(entry.ownerPersonId)) &&
      (!this.hcm.columnIsVisible('temperature') || this.isTemperatureFilterMenuItemChecked(entry.temperature)) &&
      (!this.hcm.columnIsVisible('referralType') ||
        this.isReferralTypeFilterMenuItemChecked(entry.referralSourceCategory))
    );
  }

  isOwnerFilterMenuItemChecked(personId) {
    const matchingItem = this.ownerFilterMenuItems.find((item) => item.personId === personId);
    return matchingItem && matchingItem.checked;
  }

  isTemperatureFilterMenuItemChecked(temperature) {
    const matchingItem = this.temperatureFilterMenuItems.find((item) => item.name === temperature);
    return matchingItem && matchingItem.checked;
  }

  isReferralTypeFilterMenuItemChecked(referralType) {
    const matchingItem = this.referralTypeFilterMenuItems.find((item) => item.name === referralType);
    return matchingItem && matchingItem.checked;
  }

  confirmAndRemoveInquiry(inquiryId) {
    const q = 'Are you sure you want to delete this Lead?';
    this.setState({
      shouldShowDeletionConfirmationModal: true,
      deletionConfirmationQuery: q,
      deleteFunc: () => this.removeInquiryAndClearConfirmationModal(inquiryId),
    });
  }

  removeInquiryAndClearConfirmationModal(inquiryId) {
    const uri = `/api/inquiry/${inquiryId}`;
    const request = createStdRequest(uri, 'DELETE');
    const actionOnGoodResponse = () => {
      this.props.removeInquiry(inquiryId);
      this.setState({ shouldShowDeletionConfirmationModal: false });
      this.sendReduxUpdateToDeleteInquiryToWebSocket(inquiryId);
    };
    apiFetch(this, request, actionOnGoodResponse);
  }

  sendReduxUpdateToDeleteInquiryToWebSocket(inquiryId) {
    const objToSend = {
      kind: 'inquiry',
      operation: 'delete',
      inquiryId: inquiryId,
    };
    SocketAccess.sendReduxUpdateToSocket(objToSend);
  }

  handleColumnToggle(label, checked) {
    this.hcm.setVisibilityOfColumnWithLabel(label, checked);
    this.setState({ visibilityToggle: !this.state.visibilityToggle });
  }

  setOrderedHideableColumnIdsFromItems(permutationOfTextCheckedPairs) {
    this.hcm.setOrderedHideableColumnIdsFromItems(permutationOfTextCheckedPairs);
    this.setState({ visibilityToggle: !this.state.visibilityToggle });
  }

  getPreferredFullNameOfContactOfInquiry(inquiry) {
    const personId = inquiry.referralContactId;
    const person = DA.getPersonById(personId);
    if (person) {
      return person.preferredFullName;
    } else {
      console.log();
      return `Mystery Contact ${personId}`;
    }
  }

  render() {
    if (isNonLocalNonLoggedInVisitor()) {
      return <Redirect to="/homeDispatcher" />;
    }

    if (this.state.redirect) {
      return <Redirect to={this.state.redirect} push />;
    }

    const { classes } = this.props;
    const { order, columnToOrderBy, filterItemList } = this.state;
    const inquiries = DA.getAllInquiries();
    const filteredInquiries = this.applyFilter(inquiries);
    const filtered =
      (this.hcm.columnIsVisible('referralType') &&
        !filterIsInDefaultState(this.referralTypeFilterMenuItems, this.defaultReferralTypeFilterMenuItems)) ||
      (this.hcm.columnIsVisible('temperature') &&
        !filterIsInDefaultState(this.temperatureFilterMenuItems, this.defaultTemperatureFilterMenuItems)) ||
      (this.hcm.columnIsVisible('owner') && !this.filterItemsAreAllSelected(this.ownerFilterMenuItems));
    const quantifier = filtered ? 'Filtered' : 'All';
    const summary = `${quantifier} Leads (${filteredInquiries.length})`;
    const sortedInquiries = stableSort(filteredInquiries, getSortingFunction(order, columnToOrderBy));

    const StyledFilterDialog = styled(Dialog)`
      .MuiBackdrop-root {
        background-color: transparent;
      },
    `;

    const filterPosition = {
      position: 'absolute',
      left: this.state.filterX + 'px',
      top: this.state.filterY + 'px',
      margin: '5px',
      boxShadow: '0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)',
    };
    const filterStyle = { style: filterPosition };

    const hideableColumnNamesAndCheckedState = this.hcm.getHideableColumnNamesAndCheckedState();
    // => [{name: 'Temp', checked: false}, {name: 'Message', checked: true}]

    // console.log('In render()');
    // console.log(hideableColumnNamesAndCheckedState);

    return (
      <div>
        <Paper className={classes.root}>
          <NewInquiryModal
            show={this.state.shouldShowNewInquiryModal}
            closeModalFunc={() => this.closeNewInquiryModal()}
            callRailCallId={0}
          />

          <InquiryConversionModal
            show={this.state.shouldShowConversionModal}
            convertFunc={(conversionType) => this.convertInquiry(conversionType)}
            closeFunc={() => this.setState({ shouldShowConversionModal: false })}
          />

          <ConfirmModal
            show={this.state.shouldShowDeletionConfirmationModal}
            title="Confirm Deletion"
            confirmButtonLabel="Delete"
            rejectButtonLabel="Cancel"
            onConfirm={this.state.deleteFunc}
            onReject={() => this.setState({ shouldShowDeletionConfirmationModal: false })}
          >
            <div>{this.state.deletionConfirmationQuery}</div>
          </ConfirmModal>

          <InquiryTableAppBar
            summary={summary}
            newButtonLabel="New Lead"
            newButtonFunc={() => this.showNewInquiryModal()}
            inquiries={inquiries}
            onAutocompleteChange={(inquiry) => this.handleInquiryClick(inquiry)}
            hideableColumnNamesAndCheckedState={hideableColumnNamesAndCheckedState}
            onColumnToggle={(label, checked) => this.handleColumnToggle(label, checked)}
            onColumnReorder={(permutation) => this.setOrderedHideableColumnIdsFromItems(permutation)}
          />
          <div className={classes.tableWrapper}>
            <Table stickyHeader className={classes.table} aria-labelledby="tableTitle">
              <StyledTableHead
                hcm={this.hcm}
                order={order}
                orderBy={columnToOrderBy}
                onRequestSort={this.handleRequestSort}
                onRequestFilter={this.handleRequestFilter}
                shouldHighlightFilterIcon={(columnId) => this.shouldHighlightFilterIcon(columnId)}
              />
              <TableBody>
                {sortedInquiries.map((entry, i) => {
                  return (
                    <TableRow
                      tabIndex={-1}
                      key={entry.inquiryId}
                      id={'inquiry-row-' + entry.inquiryId}
                      style={{ backgroundColor: i % 2 ? '#f7f7f7' : '#fff' }}
                    >
                      {this.hcm.visibleColumnDescrips.map((columnDescrip, j) => {
                        const columnId = columnDescrip.id;
                        switch (columnId) {
                          case 'name':
                            return (
                              <TableCell
                                key={j}
                                className={classes.clickableCell}
                                component="th"
                                scope="row"
                                padding="normal"
                                onClick={() => this.handleInquiryClick(entry)}
                              >
                                {entry.name}
                              </TableCell>
                            );
                          case 'date':
                            return (
                              <TableCell key={j} align="right">
                                {dateStringForDateTime(entry.date)}
                              </TableCell>
                            );

                          case 'owner':
                            return (
                              <TableCell key={j}>
                                {entry.ownerPersonId
                                  ? DA.getPersonById(entry.ownerPersonId).preferredFullName
                                  : 'Unassigned'}
                              </TableCell>
                            );

                          case 'temperature':
                            return <TableCell key={j}>{entry.temperature}</TableCell>;

                          case 'referralType':
                            return <TableCell key={j}>{entry.referralSourceCategory}</TableCell>;

                          case 'referralOrgType':
                            return <TableCell key={j}>{entry.referralOrgType}</TableCell>;

                          case 'referralCompany':
                            return (
                              <TableCell key={j}>
                                {entry.referralOrgId ? DA.getFirmById(entry.referralOrgId).name : null}
                              </TableCell>
                            );

                          case 'referralContact':
                            return (
                              <TableCell key={j}>
                                {entry.referralContactId ? this.getPreferredFullNameOfContactOfInquiry(entry) : null}
                              </TableCell>
                            );

                          case 'message':
                            if (entry.message.length > 80) {
                              return (
                                <Tooltip
                                  title={entry.message}
                                  classes={{ tooltip: classes.messageTooltip }}
                                  enterDelay={1000}
                                >
                                  <TableCell key={j} className={classes.messageCell} maxWidth="50%">
                                    {entry.message.slice(0, 80) + '...'}
                                  </TableCell>
                                </Tooltip>
                              );
                            } else {
                              return (
                                <TableCell key={j} className={classes.messageCell}>
                                  {entry.message}
                                </TableCell>
                              );
                            }

                          case 'actions':
                            return (
                              <TableCell key={j}>
                                <Tooltip
                                  id="tooltip-convert"
                                  key={0}
                                  title="Convert to Prospect or Contact"
                                  placement="top"
                                  classes={{ tooltip: classes.tooltip }}
                                >
                                  <Button
                                    key="convert"
                                    color="primary"
                                    className={classes.actionButton}
                                    onClick={() => this.showConversionModal(entry.inquiryId)}
                                  >
                                    <ConvertIcon className={classes.icon + ' ' + classes.convertIcon} />
                                  </Button>
                                </Tooltip>
                                <Tooltip
                                  id="tooltip-delete"
                                  key={1}
                                  title="Delete Lead"
                                  placement="top"
                                  classes={{ tooltip: classes.tooltip }}
                                >
                                  <Button
                                    key="delete"
                                    color="secondary"
                                    className={classes.actionButton}
                                    onClick={() => this.confirmAndRemoveInquiry(entry.inquiryId)}
                                  >
                                    <DeleteIcon className={classes.icon + ' ' + classes.deleteIcon} />
                                  </Button>
                                </Tooltip>
                              </TableCell>
                            );
                          default:
                            return null;
                        }
                      })}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </Paper>

        <StyledFilterDialog
          open={this.state.showFilter}
          PaperComponent={Paper}
          PaperProps={filterStyle}
          onEntered={handleEntered}
        >
          <ClickAwayListener onClickAway={() => this.handleFilterClickAway()}>
            <List>
              {filterItemList.map((item, i) => (
                <ListItem className={classes.filterListItem} key={i}>
                  <Typography variant="caption" display="block" gutterBottom>
                    {i === 0 && (
                      <Button
                        className={classes.selectAllButton}
                        onClick={() => this.handleSelectAllButtonClick(item.name)}
                      >
                        {item.name}
                      </Button>
                    )}
                    {i > 0 && (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={item.checked}
                            onChange={() => this.handleFilterItemChange(i)}
                            value={item.name}
                            color="primary"
                          />
                        }
                        label={item.name}
                        className={classes.filterLabel}
                      />
                    )}
                  </Typography>
                </ListItem>
              ))}
            </List>
          </ClickAwayListener>
        </StyledFilterDialog>
      </div>
    );
  }
}

InquiryTable.propTypes = {
  classes: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  reduxPropertyFiltersState: state.filterStates.propertyFilters,
});

const actionProducers = {
  removeInquiry,
  setApiResponseInfo,
  setIntendedInquiryId,
  setInitialCallFormPrimingInfo,
  setPropertyFilterState,
};

export default connect(mapStateToProps, actionProducers)(withStyles(styles)(InquiryTable));
